import {
  Criteria_Group_Sorting_Config,
  Criteria_Group_Sorting_Config_Bool_Exp,
  Criteria_Group_Sorting_Config_Set_Input,
  Criteria_Sorting_Config,
  Criteria_Sorting_Config_Bool_Exp,
  Criteria_Sorting_Config_Set_Input,
  Mutation_Root,
} from 'src/@types/graphql'
import {
  updateSortOnCriteriaConfig,
  updateSortOnCriteriaGroupConfig,
} from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { PROCESS, PROCESS_TYPE } from 'src/shared/constants/constants'
import { Client, useClient } from 'urql'

const switchCriteriasPosition =
  (urqlClient: Client) => async (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config, process: PROCESS_TYPE) => {
    if (c1.sort_order === c2.sort_order) {
      return Promise.reject(new Error())
    }

    const setClauseCriteria1: Criteria_Sorting_Config_Set_Input = {
      sort_order: c2.sort_order,
    }

    const setClauseCriteria2: Criteria_Sorting_Config_Set_Input = {
      sort_order: c1.sort_order,
    }

    // these should be the same for both criterias
    const criteriaType = c1.criteria_type
    const groupId = c1.group_id
    const level = c1.level
    const applicationTypeId = c1.application_type_id

    const updateFirstWhere: Criteria_Sorting_Config_Bool_Exp = {
      _and: [
        { criteria_type: { _eq: criteriaType } },
        { process: { _eq: process } },
        { criteria_id: { _eq: c1.criteria_id } },
        { group_id: { _eq: groupId } },
      ],
    }

    const updateSecondWhere: Criteria_Sorting_Config_Bool_Exp = {
      _and: [
        { criteria_type: { _eq: criteriaType } },
        { process: { _eq: process } },
        { criteria_id: { _eq: c2.criteria_id } },
        { group_id: { _eq: groupId } },
      ],
    }

    if (process === PROCESS.FACTSHEET) {
      updateFirstWhere?._and?.push({ level: { _eq: level } })
      updateSecondWhere?._and?.push({ level: { _eq: level } })
    } else {
      updateFirstWhere?._and?.push({ application_type_id: { _eq: applicationTypeId } })
      updateSecondWhere?._and?.push({ application_type_id: { _eq: applicationTypeId } })
    }

    return urqlClient
      .mutation<
        {
          updateFirst: Mutation_Root['update_criteria_sorting_config']
          updateSecond: Mutation_Root['update_criteria_sorting_config']
        },
        {
          firstWhere: Criteria_Sorting_Config_Bool_Exp
          secondWhere: Criteria_Sorting_Config_Bool_Exp
          setClause1: Criteria_Sorting_Config_Set_Input
          setClause2: Criteria_Sorting_Config_Set_Input
        }
      >(updateSortOnCriteriaConfig, {
        firstWhere: updateFirstWhere,
        secondWhere: updateSecondWhere,
        setClause1: setClauseCriteria1,
        setClause2: setClauseCriteria2,
      })
      .toPromise()
  }

export const switchCriteriaGroupsPosition =
  (urqlClient: Client) =>
  async (cg1: Criteria_Group_Sorting_Config, cg2: Criteria_Group_Sorting_Config, process: PROCESS_TYPE) => {
    if (cg1.sort_order === cg2.sort_order) {
      return Promise.reject(new Error())
    }

    const setClauseGroup1: Criteria_Group_Sorting_Config_Set_Input = {
      sort_order: cg2.sort_order,
    }

    const setClauseGroup2: Criteria_Group_Sorting_Config_Set_Input = {
      sort_order: cg1.sort_order,
    }

    // these should be the same for both criteria groups
    const criteriaType = cg1.criteria_type
    const level = cg1.level
    const applicationTypeId = cg1.application_type_id

    const updateFirstWhere: Criteria_Group_Sorting_Config_Bool_Exp = {
      _and: [
        { criteria_type: { _eq: criteriaType } },
        { process: { _eq: process } },
        { group_id: { _eq: cg1.group_id } },
      ],
    }

    const updateSecondWhere: Criteria_Group_Sorting_Config_Bool_Exp = {
      _and: [
        { criteria_type: { _eq: criteriaType } },
        { process: { _eq: process } },
        { group_id: { _eq: cg2.group_id } },
      ],
    }

    if (process === PROCESS.FACTSHEET) {
      updateFirstWhere?._and?.push({ level: { _eq: level } })
      updateSecondWhere?._and?.push({ level: { _eq: level } })
    } else {
      updateFirstWhere?._and?.push({ application_type_id: { _eq: applicationTypeId } })
      updateSecondWhere?._and?.push({ application_type_id: { _eq: applicationTypeId } })
    }

    return urqlClient
      .mutation<
        {
          updateFirst: Mutation_Root['update_criteria_sorting_config']
          updateSecond: Mutation_Root['update_criteria_sorting_config']
        },
        {
          firstWhere: Criteria_Group_Sorting_Config_Bool_Exp
          secondWhere: Criteria_Group_Sorting_Config_Bool_Exp
          setClause1: Criteria_Group_Sorting_Config_Set_Input
          setClause2: Criteria_Group_Sorting_Config_Set_Input
        }
      >(updateSortOnCriteriaGroupConfig, {
        firstWhere: updateFirstWhere,
        secondWhere: updateSecondWhere,
        setClause1: setClauseGroup1,
        setClause2: setClauseGroup2,
      })
      .toPromise()
  }

export const useSortCriterias = () => {
  const urqlClient = useClient()
  return {
    switchCriteriaGroupsPosition: switchCriteriaGroupsPosition(urqlClient),
    switchCriteriasPosition: switchCriteriasPosition(urqlClient),
  }
}
